import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Bell, CalendarClock, GraduationCap, UserCheck } from 'lucide-react';
import { PortalSummary } from '../../types/portal';
import { DashboardCard, DashboardEmptyState, DashboardSectionTitle } from './DashboardShared';

const fa = (n: number) => n.toLocaleString('fa-IR');

const HEALTH: Record<string, { label: string; color: string; hint: string }> = {
  GREEN: { label: 'در مسیر', color: '#0ca30c', hint: 'تمرین‌ها به‌موقع پیش می‌روند.' },
  YELLOW: {
    label: 'نیازمند توجه',
    color: '#fab219',
    hint: 'یک تمرین عقب افتاده یا مدتی وارد پرتال نشده‌اید.',
  },
  RED: {
    label: 'عقب‌افتاده',
    color: '#d03b3b',
    hint: 'دو تمرین یا بیشتر عقب افتاده — منتور شما هم خبر دارد.',
  },
};

/**
 * کارت خلاصهٔ پرتال در داشبورد سایت.
 *
 * عمداً خودِ پرتال اینجا رندر نمی‌شود؛ پرتال داشبورد کامل خودش را دارد و جا
 * دادنش داخل این صفحه یعنی دو لایه داشبورد تودرتو. این کارت فقط وضعیت را
 * می‌گوید و راه را نشان می‌دهد.
 */
export const DashboardPortalPanel: React.FC<{ summary: PortalSummary | null }> = ({ summary }) => {
  if (!summary) {
    return (
      <DashboardEmptyState
        title="شما در دورهٔ کوچینگ ثبت‌نام نشده‌اید"
        subtitle="اگر در دورهٔ کوچینگ ۶C شرکت کرده‌اید و این بخش را نمی‌بینید، با پشتیبانی تماس بگیرید."
      />
    );
  }

  const health = HEALTH[summary.health] ?? HEALTH.GREEN;
  const due = summary.nextDue?.dueAt ? new Date(summary.nextDue.dueAt) : null;
  const overdue = due ? due.getTime() < Date.now() : false;

  return (
    <div className="space-y-4">
      <div className="rounded-3xl border border-[#DDE5FF] bg-[linear-gradient(145deg,#FFFFFF_0%,#F7FAFF_100%)] p-4 shadow-[0_14px_30px_-20px_rgba(28,59,155,0.35)] sm:p-5">
        <DashboardSectionTitle
          title={summary.programTitle}
          subtitle={
            summary.cohortName + (summary.mentorName ? ' · منتور: ' + summary.mentorName : '')
          }
          accent="violet"
          action={
            <Link
              to="/portal"
              className="inline-flex items-center gap-1.5 rounded-xl bg-[#1C3B9B] px-4 py-2 text-xs font-black text-white shadow-[0_10px_22px_-14px_rgba(28,59,155,0.6)] transition hover:-translate-y-0.5"
            >
              ورود به پرتال
              <ArrowLeft className="h-3.5 w-3.5" />
            </Link>
          }
        />

        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
          <div className="rounded-2xl border border-[#E6ECFA] bg-white px-3 py-3 text-center">
            <p className="text-[10px] font-bold text-slate-500">پیشرفت</p>
            <p className="mt-1 text-lg font-black text-slate-900">{fa(summary.progressPercent)}٪</p>
          </div>
          <div
            className="rounded-2xl border bg-white px-3 py-3 text-center"
            style={{ borderColor: health.color + '44' }}
          >
            <p className="text-[10px] font-bold text-slate-500">وضعیت</p>
            <p className="mt-1 text-sm font-black" style={{ color: health.color }}>
              {health.label}
            </p>
          </div>
          <div
            className={
              'rounded-2xl border px-3 py-3 text-center ' +
              (summary.urgent ? 'border-[#F3C9C9] bg-[#FDF2F2]' : 'border-[#E6ECFA] bg-white')
            }
          >
            <p className="text-[10px] font-bold text-slate-500">کار فوری</p>
            <p
              className="mt-1 text-lg font-black"
              style={{ color: summary.urgent ? '#8E2020' : '#0F172A' }}
            >
              {fa(summary.urgent)}
            </p>
          </div>
          <div className="rounded-2xl border border-[#E6ECFA] bg-white px-3 py-3 text-center">
            <p className="text-[10px] font-bold text-slate-500">در جریان</p>
            <p className="mt-1 text-lg font-black text-slate-900">{fa(summary.pending)}</p>
          </div>
        </div>

        <p className="mt-3 text-[11.5px] font-bold leading-6 text-slate-500">{health.hint}</p>
      </div>

      {summary.nextDue ? (
        <DashboardCard className="border-[#DDE5FF]">
          <DashboardSectionTitle
            title="نزدیک‌ترین کار شما"
            subtitle={overdue ? 'مهلت این تمرین گذشته است.' : 'این تمرین در نوبت شماست.'}
            accent="violet"
          />
          <Link
            to={'/portal/tasks/' + summary.nextDue.assignmentId}
            className="flex items-center gap-3 rounded-2xl border border-[#E6ECFA] bg-white p-3.5 transition hover:-translate-y-0.5 hover:border-[#9DB0E4]"
          >
            <span
              aria-hidden
              className="h-10 w-1 shrink-0 rounded-full"
              style={{ backgroundColor: overdue ? '#d03b3b' : '#1C3B9B' }}
            />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13.5px] font-black text-slate-900">
                {summary.nextDue.title}
              </span>
              {due ? (
                <span className="mt-0.5 flex items-center gap-1 text-[11px] font-bold text-slate-400">
                  <CalendarClock className="h-3.5 w-3.5" />
                  مهلت: {due.toLocaleDateString('fa-IR')}
                </span>
              ) : null}
            </span>
            {overdue ? (
              <span className="shrink-0 rounded-full border border-[#F3C9C9] bg-[#FDF2F2] px-2.5 py-1 text-[10px] font-black text-[#8E2020]">
                عقب‌افتاده
              </span>
            ) : null}
            <ArrowLeft className="h-4 w-4 shrink-0 text-slate-300" />
          </Link>
        </DashboardCard>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-3">
        {[
          {
            to: '/portal',
            icon: <GraduationCap className="h-5 w-5" />,
            title: 'جلسات و جزوه‌ها',
            hint: 'محتوای هر جلسه و دانلود فایل‌ها',
          },
          {
            to: '/portal/questions',
            icon: <UserCheck className="h-5 w-5" />,
            title: 'پرسش از منتور',
            hint: 'سؤالتان را مستقیم بپرسید',
          },
          {
            to: '/portal/support',
            icon: <Bell className="h-5 w-5" />,
            title: 'پشتیبانی',
            hint: summary.unreadNotifications
              ? fa(summary.unreadNotifications) + ' اعلان نخوانده'
              : 'ثبت تیکت و پیگیری',
          },
        ].map((item) => (
          <Link
            key={item.title}
            to={item.to}
            className="flex items-center gap-3 rounded-2xl border border-[#E6ECFA] bg-white p-4 transition hover:-translate-y-0.5 hover:border-[#9DB0E4]"
          >
            <span className="shrink-0 text-[#1C3B9B]">{item.icon}</span>
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] font-black text-slate-900">{item.title}</span>
              <span className="text-[11px] font-bold text-slate-400">{item.hint}</span>
            </span>
          </Link>
        ))}
      </div>

      {summary.urgent ? (
        <div className="flex items-start gap-2 rounded-2xl border border-[#F3C9C9] bg-[#FDF2F2] px-4 py-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[#8E2020]" />
          <p className="text-[12px] font-bold leading-7 text-[#8E2020]">
            {fa(summary.urgent)} تمرین منتظر شماست که مهلتش گذشته یا منتور خواسته اصلاحش کنید.
            هرچه زودتر رسیدگی کنید، وضعیت شما دوباره سبز می‌شود.
          </p>
        </div>
      ) : null}
    </div>
  );
};
